import { Badge, BrandMark, Card, Container, Text, onLinkClick } from '@cottonray/ui-web'
import { StudioHome } from './StudioHome'
import {
  BRAND,
  BUSINESS,
  CONTACT_EMAIL,
  type Service,
} from './content'
// 서비스 아이콘은 각 서비스의 brand/export가 단일 출처다 — 여기로 복사해 두면
// 스토어 아이콘이 바뀔 때 사이트만 낡는다. esbuild가 해시를 붙여 dist로 가져간다.
import easyAirScreenIcon from '../../../easy-air-screen/brand/export/icon-192.png'
import easyAirScreenLiteIcon from '../../../easy-air-screen/brand/export/icon-lite-192.png'
import soundtetherIcon from '../../../soundtether/brand/export/icon-192.png'
import peekmoIcon from '../../../peekmo/brand/export/icon-192.png'
import velariaIcon from '../../../velaria/brand/export/icon-192.png'
import soulweldIcon from '../../../../game/soulweld/icon.png'

const SERVICE_ICONS: Record<string, string> = {
  'easy-air-screen': easyAirScreenIcon,
  'easy-air-screen-lite': easyAirScreenLiteIcon,
  soundtether: soundtetherIcon,
  peekmo: peekmoIcon,
  velaria: velariaIcon,
  soulweld: soulweldIcon,
}

export function Home() {
  return <StudioHome icons={SERVICE_ICONS} />
}

/** 홈 §서비스와 /services 페이지가 같은 카드를 쓴다 — 두 곳이 다르게 생기면 안 된다 */
export function ServiceCard({ service }: { service: Service }) {
  const icon = SERVICE_ICONS[service.slug]

  /*
    **카드 전체를 링크로 감싸던 것을 링크 줄로 바꿨다 (2026-08-24).**
    앱이 App Store 와 Play 양쪽에 올라가면서 카드 하나에 갈 곳이 둘 이상이 됐다 —
    `<a>` 안에 `<a>` 는 넣을 수 없고, 스토어 하나만 걸면 나머지 플랫폼 방문자는
    사이트에서 앱을 받을 길이 없다. 그래서 갈 곳을 전부 버튼으로 드러낸다.
  */
  const destinations: { href: string; label: string; primary: boolean }[] = [
    ...(service.link === undefined
      ? []
      : [{ href: service.link.href, label: service.link.label, primary: true }]),
    ...(service.stores?.ios === undefined
      ? []
      : [{ href: service.stores.ios, label: 'App Store', primary: false }]),
    ...(service.stores?.android === undefined
      ? []
      : [{ href: service.stores.android, label: 'Google Play', primary: false }]),
  ]

  return (
    <Card className="svc-card">
      <div className="svc-card__top">
        {icon !== undefined && (
          <img className="svc-card__icon" src={icon} alt="" width={48} height={48} />
        )}
        <div className="svc-card__title">
          <Text variant="heading" as="span">
            {service.name}
          </Text>
        </div>
        {/* 지금 쓸 수 있는 것(Paid·Free)만 강조한다 — In development 는 눌러도 갈 곳이 없다 */}
        <Badge tone={service.status === 'In development' ? 'neutral' : 'success'}>
          {service.status}
        </Badge>
      </div>

      <Text tone="muted" className="svc-card__desc">
        {service.description}
      </Text>

      {destinations.length > 0 && (
        <div className="svc-card__links">
          {destinations.map((destination) => {
            const external = destination.href.startsWith('http')
            return (
              <a
                key={destination.href}
                className={`st-btn st-btn--sm ${destination.primary ? 'st-btn--primary' : 'st-btn--secondary'}`}
                href={destination.href}
                // 화면에는 "App Store" 만 보이지만 카드가 아홉이라 그것만으로는 어느 앱인지
                // 알 수 없다 — 링크만 훑는 사람에게는 앱 이름이 붙어 있어야 한다
                aria-label={`${service.name} — ${destination.label}`}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={external ? undefined : onLinkClick}
              >
                {destination.label}
              </a>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function Wordmark() {
  return (
    <a className="wordmark" href="/" onClick={onLinkClick}>
      <BrandMark />
      {/* 글자와 점은 한 덩어리다 — 풀어 놓으면 마크와 벌리려고 준 flex gap이 점 앞에도 들어간다 */}
      <span className="wordmark__text">
        cottonray
        <span className="wordmark__dot">.</span>
      </span>
    </a>
  )
}

export function SiteHeader() {
  return (
    <header className="site-header">
      <Container className="site-header__inner">
        <Wordmark />
        <nav className="site-nav" aria-label="Main navigation">
          <a href="/#games">Games</a>
          <a href="/#services">Apps</a>
          <a href="/#principles">Studio</a>
          <a href="/#contact">Contact</a>
        </nav>
      </Container>
    </header>
  )
}

/**
 * 전자상거래법 제10조 표시 사항 — 상호·대표자명·사업자등록번호.
 *
 * **등록된 한국어 표기를 괄호에서 지우지 않는다.** 제10조가 요구하는 것은 등록부에 있는
 * 그 표기이고, 로마자만 남기면 사이버몰의 표시가 등록과 어긋난다. 사이트 언어가 영어라
 * 읽을 수 있게 로마자를 앞에 두고 원문을 괄호로 붙인다 (`content.ts` §BUSINESS).
 */
const BUSINESS_LINES = [
  `${BUSINESS.nameRoman} (${BUSINESS.name})`,
  `Representative ${BUSINESS.representativeRoman} (${BUSINESS.representative})`,
  `Business registration number ${BUSINESS.registrationNumber}`,
]

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <Container>
        <div className="site-footer__top">
          <Wordmark />
          <div className="site-footer__links">
            <a href="/privacy" onClick={onLinkClick}>
              Privacy policy
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </div>

        <ul className="site-footer__business">
          {BUSINESS_LINES.map((line) => (
            <li key={line}>
              <Text variant="caption" tone="subtle" as="span">
                {line}
              </Text>
            </li>
          ))}
        </ul>
        <Text variant="caption" tone="subtle">
          © {new Date().getFullYear()} {BUSINESS.nameRoman} ({BUSINESS.name})
        </Text>
      </Container>
    </footer>
  )
}
